// ── Shared browser Sentry initialization (dependency-injected) ─────────────
//
// Centralizes the init shape + redaction/PII defaults that the VJAL frontends
// previously did unevenly (some apps shipped a bare `Sentry.init`, one app added
// bespoke capability-token redaction). The Sentry SDK itself is INJECTED by the
// caller (`sentry` option) so this package never has to depend on a specific
// `@sentry/react` major (apps currently span v8 and v10) and stays framework-free.
//
// Behavior contract (preserved by every caller):
//   - init is SKIPPED when no DSN is configured (returns false);
//   - the caller supplies its own dsn, environment, tracing/replay sample rates,
//     integrations, and tags — this helper does NOT invent or change sampling;
//   - the helper layers in a no-secrets default scrub (auth/cookie headers are
//     stripped from the outgoing event — a no-op for current browser telemetry,
//     defense-in-depth) and runs any app-specific `redact` transform on BOTH the
//     error (`beforeSend`) and transaction (`beforeSendTransaction`) paths.
//
// Security: a Sentry DSN is a public, non-secret value. This helper never
// attaches auth tokens, cookies, or provider creds to events; the default scrub
// only ever REMOVES sensitive fields, it never adds any.

/**
 * Minimal structural view of the injected Sentry SDK. Declared as a METHOD so
 * TypeScript checks the parameter bivariantly, which lets the concrete
 * `@sentry/react` namespace (`Sentry`, whose `init` is typed against its own
 * `BrowserOptions`) be passed without a cast across SDK majors.
 */
export interface SentrySdk {
  init(options: Record<string, unknown>): unknown
}

/**
 * A Sentry event slice this helper may touch. Intentionally loose: callers and
 * the SDK own the full event type, we only read/scrub a few well-known fields.
 */
export interface ScrubbableSentryEvent {
  request?: {
    headers?: Record<string, unknown>
    cookies?: unknown
    [key: string]: unknown
  }
  [key: string]: unknown
}

export interface InitFrontendSentryOptions {
  /** The Sentry SDK (e.g. the `@sentry/react` namespace). Dependency-injected. */
  sentry: SentrySdk
  /**
   * Public Sentry DSN. When falsy (undefined / empty string) init is SKIPPED —
   * this is the shared form of every app's "only init when a DSN is configured"
   * guard.
   */
  dsn?: string | null
  /** Deployment environment tag (apps pass `import.meta.env.MODE` or their APP_ENV). */
  environment?: string
  /** Release identifier, when the app supplies one. */
  release?: string
  /** Performance/tracing sample rate. Passed through unchanged — never defaulted. */
  tracesSampleRate?: number
  /** Session-replay sample rate. Passed through unchanged when provided. */
  replaysSampleRate?: number
  /** Maps to Sentry `sendDefaultPii`; defaults to `false` (Sentry's own default). */
  sendDefaultPii?: boolean
  /** Init-scope tags. Applied via `initialScope.tags` when provided. */
  tags?: Record<string, string | number | boolean | null | undefined>
  /** Integrations (e.g. `[Sentry.browserTracingIntegration()]`). Passed through. */
  integrations?: unknown[]
  /**
   * Optional app-specific redaction run on every outgoing event (errors AND
   * transactions), AFTER the shared default scrub. Receives the event and returns
   * the event to send (it typically mutates in place and returns the same
   * reference, exactly as a Sentry `beforeSend` would). Used e.g. by the
   * consulting/forms app to strip the public-fill capability token from
   * URL-bearing fields. Return type is intentionally loose so callers can hand in
   * their own narrower event type without a cast.
   */
  redact?: (event: ScrubbableSentryEvent) => unknown
}

/**
 * Header keys that must never leave the browser inside a Sentry event. The
 * browser SDK does not populate these for page/error events (it cannot read
 * httpOnly cookies and does not attach Authorization), so stripping them is a
 * no-op for today's telemetry — but it makes the default safe if an app ever
 * attaches request context.
 */
const SENSITIVE_HEADER_KEYS = new Set([
  'authorization',
  'proxy-authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'x-csrf-token',
  'x-vjal-csrf',
])

/**
 * Strip auth/cookie headers and request cookies from an event in place. Returns
 * the same reference. Tolerant of sparse/partial events.
 */
export function scrubSensitiveEvent<T extends ScrubbableSentryEvent>(event: T): T {
  if (!event || typeof event !== 'object') return event
  const request = event.request
  if (request && typeof request === 'object') {
    const headers = request.headers
    if (headers && typeof headers === 'object') {
      for (const key of Object.keys(headers)) {
        if (SENSITIVE_HEADER_KEYS.has(key.toLowerCase())) delete headers[key]
      }
    }
    if ('cookies' in request) delete request.cookies
  }
  return event
}

/**
 * Initialize browser Sentry with the shared redaction/PII defaults.
 *
 * @returns `true` when `sentry.init` was called, `false` when skipped (no DSN).
 */
export function initFrontendSentry(options: InitFrontendSentryOptions): boolean {
  const {
    sentry,
    dsn,
    environment,
    release,
    tracesSampleRate,
    replaysSampleRate,
    sendDefaultPii,
    tags,
    integrations,
    redact,
  } = options

  // Shared DSN guard: never init without a configured (public) DSN.
  if (!dsn) return false

  const beforeSend = (event: ScrubbableSentryEvent): ScrubbableSentryEvent => {
    const scrubbed = scrubSensitiveEvent(event)
    // Redactors mutate-and-return the same event (Sentry `beforeSend` contract);
    // we forward whatever they return, preserving each app's prior semantics.
    return (redact ? redact(scrubbed) : scrubbed) as ScrubbableSentryEvent
  }

  const initOptions: Record<string, unknown> = {
    dsn,
    sendDefaultPii: sendDefaultPii ?? false,
    beforeSend,
    beforeSendTransaction: beforeSend,
  }
  if (environment !== undefined) initOptions.environment = environment
  if (release !== undefined) initOptions.release = release
  if (tracesSampleRate !== undefined) initOptions.tracesSampleRate = tracesSampleRate
  if (replaysSampleRate !== undefined) initOptions.replaysSampleRate = replaysSampleRate
  if (integrations !== undefined) initOptions.integrations = integrations
  if (tags !== undefined) initOptions.initialScope = { tags }

  sentry.init(initOptions)
  return true
}
